import lawImg from "@/assets/industry-law.jpg";
import medImg from "@/assets/industry-medical.jpg";
import reImg from "@/assets/industry-realestate.jpg";
import fitImg from "@/assets/industry-fitness.jpg";

export type FaqItem = { q: string; a: string };

export type UseCaseTool = {
  name: string;
  cost: string;
  domain: string;
  stepIndex: number;
  icon: "bot" | "calendar" | "mail" | "bell" | "file-text" | "users" | "message-square" | "zap" | "dollar-sign" | "phone";
};

export type UseCase = {
  title: string;
  hook: string;
  before: string[];
  after: string[];
  tools: UseCaseTool[];
  stats: { value: string; label: string }[];
};

export type Industry = {
  slug: "law-firms" | "medical-dental" | "real-estate" | "fitness";
  title: string;
  benefit: string;
  bullets: [string, string];
  roi: string;
  img: string;
  alt: string;
  automate: string[];
  quickWins: string[];
  steps: string[];
  faq: FaqItem[];
  cta: string;
  useCases: UseCase[];
};

export const industries: Industry[] = [
  {
    slug: "law-firms",
    title: "Law Firms",
    benefit: "Speed up contract review and client intake.",
    bullets: [
      "Automate contract summarization & precedent search",
      "24/7 AI receptionist to qualify and schedule",
    ],
    roi: "ROI: +5 hrs/week; $1k+/mo recaptured",
    img: lawImg,
    alt: "Law books on a shelf",
    automate: [
      "Contract summarization and clause extraction",
      "Precedent and case search assistants",
      "AI receptionist for intake and scheduling",
    ],
    quickWins: [
      "Cut first-draft review time by 50%",
      "Auto-route and prioritize client inquiries",
    ],
    steps: [
      "Discovery of workflows and tools",
      "Pilot an AI assistant on a narrow task",
      "Expand automation and measure ROI",
    ],
    faq: [
      { q: "Is my data confidential?", a: "Yes. We use secure, access-controlled environments and can integrate with your DMS." },
      { q: "Do you support small boutique firms?", a: "Absolutely. We tailor scope to solo and small teams with quick wins first." },
    ],
    cta: "Book a free consultation for law firms",
    useCases: [
      {
        title: "Contract Review — From 6 Hours to 45 Minutes",
        hook: "A Montclair attorney was spending 6 hours a week reviewing contracts manually. We cut it to 45 minutes.",
        before: [
          "Download contract PDF",
          "Read manually, highlight key clauses",
          "Write summary for client",
          "Search for relevant precedents",
          "Update case management system",
        ],
        after: [
          "Upload contract to AI assistant",
          "Auto-summary with flagged risk clauses (3 min)",
          "Precedent suggestions surfaced instantly",
          "Summary sent to client automatically",
        ],
        tools: [
          { name: "Spellbook", icon: "file-text", cost: "AI contract review", domain: "spellbook.legal", stepIndex: 0 },
          { name: "Clio Manage", icon: "calendar", cost: "Case management", domain: "clio.com", stepIndex: 3 },
          { name: "Lexis+ AI", icon: "zap", cost: "Precedent research", domain: "lexisnexis.com", stepIndex: 2 },
        ],
        stats: [
          { value: "87%", label: "Less time on contract review" },
          { value: "5 hrs", label: "Saved per week" },
          { value: "$89/mo", label: "Total tool cost" },
        ],
      },
      {
        title: "Client Intake — From 4 Hours to 12 Minutes",
        hook: "New inquiries sat in an inbox for hours. Now every lead gets a response in under 15 minutes — automatically.",
        before: [
          "Inquiry arrives via email or voicemail",
          "Attorney or staff manually reviews",
          "Phone tag to schedule consultation",
          "Intake form emailed as PDF",
          "Info manually entered into system",
        ],
        after: [
          "AI receptionist qualifies inquiry instantly",
          "Prospect self-books via calendar link",
          "Intake form completed on their phone",
          "Info auto-populated into case management",
        ],
        tools: [
          { name: "Lawmatics", icon: "message-square", cost: "Intake & CRM", domain: "lawmatics.com", stepIndex: 0 },
          { name: "Clio Grow", icon: "users", cost: "Client onboarding", domain: "clio.com", stepIndex: 2 },
          { name: "Calendly", icon: "calendar", cost: "Consultation booking", domain: "calendly.com", stepIndex: 1 },
        ],
        stats: [
          { value: "12 min", label: "Avg response time (was 4 hrs)" },
          { value: "2×", label: "More consultations booked" },
          { value: "0", label: "Leads lost to slow follow-up" },
        ],
      },
    ],
  },
  {
    slug: "medical-dental",
    title: "Medical/Dental",
    benefit: "Reduce no-shows with smart reminders and easy intake.",
    bullets: ["HIPAA-safe reminders & eligibility checks", "Automated intake and follow-ups"],
    roi: "ROI: one missed appointment saved each week",
    img: medImg,
    alt: "Bright medical clinic hallway",
    automate: [
      "Patient intake chat and forms",
      "Insurance eligibility checks",
      "No-show reduction reminders",
    ],
    quickWins: [
      "Instant appointment confirmations",
      "Automated pre-visit instructions",
    ],
    steps: [
      "Review of EHR and scheduling tools",
      "Enable reminder workflows",
      "Add intake and eligibility automation",
    ],
    faq: [
      { q: "HIPAA compliance?", a: "We follow HIPAA best practices and can work with BAAs where required." },
      { q: "Will it replace staff?", a: "No—automation assists staff by removing repetitive tasks." },
    ],
    cta: "Book a free consultation for your practice",
    useCases: [
      {
        title: "No-Show Reduction — 18% Down to 9%",
        hook: "A Montclair dental office was losing $3,200/month to no-shows. A 3-touch reminder sequence cut that in half.",
        before: [
          "Single reminder sent 2 days before",
          "Staff calls no-shows after the fact",
          "Empty slot goes unfilled",
          "Revenue lost, slot wasted",
        ],
        after: [
          "Reminder email sent 1 week before",
          "SMS reminder + confirm link 2 days before",
          "Final reminder 2 hours before appointment",
          "No-show triggers waitlist patient automatically",
        ],
        tools: [
          { name: "NexHealth", icon: "bell", cost: "Reminders & scheduling", domain: "nexhealth.com", stepIndex: 0 },
          { name: "Weave", icon: "message-square", cost: "2-way patient texting", domain: "getweave.com", stepIndex: 1 },
          { name: "Dentrix", icon: "calendar", cost: "Practice management", domain: "dentrix.com", stepIndex: 3 },
        ],
        stats: [
          { value: "50%", label: "Fewer no-shows" },
          { value: "$1,600", label: "Revenue recovered/month" },
          { value: "20×", label: "ROI on tool cost" },
        ],
      },
      {
        title: "Patient Intake — Forms Done Before They Arrive",
        hook: "Patients were arriving late because intake forms were mailed as PDFs. Now they complete everything on their phone before the visit.",
        before: [
          "Mail or email PDF intake form",
          "Patient prints, fills out, scans back",
          "Staff transcribes into EHR manually",
          "Patient arrives and fills out paper forms",
          "Appointment starts late",
        ],
        after: [
          "Intake link texted automatically after booking",
          "Patient completes on phone in 5 minutes",
          "Responses flow directly into EHR",
          "Insurance eligibility checked automatically",
        ],
        tools: [
          { name: "Klara", icon: "file-text", cost: "Digital intake & messaging", domain: "klara.com", stepIndex: 0 },
          { name: "Availity", icon: "zap", cost: "Insurance eligibility", domain: "availity.com", stepIndex: 3 },
          { name: "athenahealth", icon: "users", cost: "EHR integration", domain: "athenahealth.com", stepIndex: 2 },
        ],
        stats: [
          { value: "15 min", label: "Saved per patient visit" },
          { value: "90%", label: "Forms complete before arrival" },
          { value: "0", label: "Manual transcription errors" },
        ],
      },
    ],
  },
  {
    slug: "real-estate",
    title: "Real Estate",
    benefit: "Qualify leads and follow up automatically.",
    bullets: [
      "AI lead-qualifying chat & auto follow-up",
      "Listing generator and next-step reminders",
    ],
    roi: "ROI: more closings with fewer empty slots",
    img: reImg,
    alt: "Suburban house exterior",
    automate: [
      "Lead capture and qualification",
      "Automated follow-ups and reminders",
      "Listing description generator",
    ],
    quickWins: ["Faster lead response times", "Fewer missed showings"],
    steps: [
      "Connect CRM and channels",
      "Deploy lead-qualifying assistant",
      "Automate follow-ups",
    ],
    faq: [
      { q: "Do you integrate with my CRM?", a: "We support popular CRMs and can add custom connectors." },
    ],
    cta: "Book a free consultation for real estate",
    useCases: [
      {
        title: "Lead Response — From 4 Hours to 90 Seconds",
        hook: "78% of buyers go with the first agent who responds. A Montclair agent was averaging a 4-hour response time. We fixed that.",
        before: [
          "Lead submits inquiry on website",
          "Agent checks email — hours later",
          "Manual follow-up call or text",
          "Lead has already contacted another agent",
          "Deal lost before it started",
        ],
        after: [
          "Lead submits inquiry",
          "AI assistant responds in 90 seconds",
          "Qualifies buyer/seller intent automatically",
          "Books showing or call via calendar link",
          "Agent gets notified of a warm, prepped lead",
        ],
        tools: [
          { name: "Follow Up Boss", icon: "users", cost: "Real estate CRM", domain: "followupboss.com", stepIndex: 1 },
          { name: "BoomTown", icon: "message-square", cost: "Lead management & follow-up", domain: "boomtownroi.com", stepIndex: 0 },
          { name: "Calendly", icon: "calendar", cost: "Showing scheduler", domain: "calendly.com", stepIndex: 3 },
        ],
        stats: [
          { value: "90 sec", label: "Lead response time (was 4 hrs)" },
          { value: "78%", label: "Buyers pick the first responder" },
          { value: "+1 closing", label: "In first 60 days" },
        ],
      },
      {
        title: "Listing Descriptions — 45 Minutes to 5 Minutes",
        hook: "Writing compelling listing descriptions was eating 45 minutes per property. AI brings that to under 5 minutes — in your voice.",
        before: [
          "Review property notes and photos",
          "Draft description from scratch",
          "Revise for tone and length",
          "Format for MLS, Zillow, social",
          "Repeat for every listing",
        ],
        after: [
          "Enter key property details",
          "AI drafts in your voice in 60 seconds",
          "Light editing if needed",
          "One-click format for each platform",
        ],
        tools: [
          { name: "ChatGPT / Claude", icon: "file-text", cost: "Listing copy", domain: "openai.com", stepIndex: 1 },
          { name: "Canva", icon: "mail", cost: "Social graphics", domain: "canva.com", stepIndex: 2 },
          { name: "kvCORE", icon: "zap", cost: "MLS syndication", domain: "kvcore.com", stepIndex: 3 },
        ],
        stats: [
          { value: "89%", label: "Less time per listing" },
          { value: "3 hrs", label: "Saved per week" },
          { value: "$35/mo", label: "Total tool cost" },
        ],
      },
    ],
  },
  {
    slug: "fitness",
    title: "Fitness",
    benefit: "Fill classes and retain members.",
    bullets: ["Predict class fill & auto booking", "Member Q&A assistant and retention"],
    roi: "ROI: +$100s/mo per retained member",
    img: fitImg,
    alt: "Modern fitness studio interior",
    automate: [
      "Waitlist and auto-book flows",
      "Member Q&A assistant",
      "Churn risk detection",
    ],
    quickWins: ["Higher class utilization", "Reduced churn"],
    steps: ["Connect booking software", "Enable waitlist automation", "Add retention assistant"],
    faq: [
      { q: "Which platforms do you support?", a: "We integrate with common booking platforms; ask us about yours." },
    ],
    cta: "Book a free consultation for fitness",
    useCases: [
      {
        title: "Class Fill Rate — From 60% to 90%",
        hook: "A Montclair gym had 8 empty spots in every class — not from low demand, but because waitlists were managed in a notebook.",
        before: [
          "Member cancels spot",
          "Staff manually checks waitlist notebook",
          "Calls or texts next person on list",
          "No response — spot stays empty",
          "Revenue lost, class runs half-empty",
        ],
        after: [
          "Member cancels spot",
          "System instantly notifies waitlist (in order)",
          "First to confirm gets the spot automatically",
          "Class fills in minutes, no staff needed",
        ],
        tools: [
          { name: "Mindbody", icon: "calendar", cost: "Class booking & waitlist", domain: "mindbodyonline.com", stepIndex: 1 },
          { name: "PushPress", icon: "bell", cost: "Studio scheduling & notifications", domain: "pushpress.com", stepIndex: 1 },
          { name: "Glofox", icon: "zap", cost: "Member app & scheduling", domain: "glofox.com", stepIndex: 2 },
        ],
        stats: [
          { value: "8 → 2", label: "Empty spots per class" },
          { value: "+$4,750", label: "Monthly revenue recovered" },
          { value: "96×", label: "ROI on tool cost" },
        ],
      },
      {
        title: "Member Retention — Churn Down 22%",
        hook: "Members who skip a week often don't come back. A simple automated check-in sequence changed that.",
        before: [
          "Member misses a week of classes",
          "Nobody notices until they cancel",
          "Last-ditch discount offer (too late)",
          "Member churns, revenue lost",
        ],
        after: [
          "System detects 3+ days of inactivity",
          "Automated 'we miss you' message sent",
          "7-day no-visit triggers guest pass offer",
          "14-day triggers personal staff outreach flag",
        ],
        tools: [
          { name: "Mindbody Marketing", icon: "mail", cost: "Retention campaigns", domain: "mindbodyonline.com", stepIndex: 1 },
          { name: "Wodify", icon: "users", cost: "Attendance tracking", domain: "wodify.com", stepIndex: 0 },
          { name: "Zen Planner", icon: "bot", cost: "Churn alerts & reporting", domain: "zenplanner.com", stepIndex: 3 },
        ],
        stats: [
          { value: "22%", label: "Reduction in churn rate" },
          { value: "$100s", label: "Saved per retained member/mo" },
          { value: "3 days", label: "To detect at-risk members" },
        ],
      },
    ],
  },
];

export function getIndustryBySlug(slug: Industry["slug"]) {
  return industries.find((i) => i.slug === slug);
}
